/**
 * Google Analytics Event Tracking Utilities
 *
 * This module provides type-safe wrappers for Google Analytics 4 event tracking.
 * Events are only sent in production or when GA_DEBUG is enabled.
 */

import { preprocessMarkdown, containsDollarMath } from "./markdown-preprocessor";
import { countWords, docSizeBucket, type DocSize } from "./doc-size";

// TypeScript declarations for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
  }
}

// Environment configuration
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const GA_DEBUG = process.env.NEXT_PUBLIC_GA_DEBUG === 'true';
const ENABLE_TRACKING = IS_PRODUCTION || GA_DEBUG;

/**
 * Base event tracking function
 * Wraps gtag with null checks and environment-aware logging
 */
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, unknown>
): void => {
  if (!ENABLE_TRACKING) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GA Event - Dev Mode]', eventName, parameters);
    }
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters);

    if (GA_DEBUG) {
      console.log('[GA Event]', eventName, parameters);
    }
  }
};

// ============================================================================
// Export Events (HIGH PRIORITY - Conversion Tracking)
// ============================================================================

/**
 * Source chat site for extension handoffs. When the browser extension opens
 * the site via the #md= handoff, the page remembers which chat site the
 * content came from (chatgpt | claude | gemini) for the session, and every
 * export event carries it as `site` — so format adoption can be split by
 * origin. Direct site visits have no `site` param.
 *
 * TRACKING RULE: any new export surface or newly supported chat site must
 * flow through this (and be covered in docs/ANALYTICS_TRACKING.md) — see
 * CLAUDE.md.
 */
const HANDOFF_SITE_KEY = 'llm2doc_handoff_site';

export const rememberHandoffSite = (site: string): void => {
  try {
    sessionStorage.setItem(HANDOFF_SITE_KEY, site);
  } catch {
    /* private mode / storage disabled — attribution is best-effort */
  }
};

/**
 * Document setup in force for this session's exports: "default" (nothing
 * changed), "custom" (page/margins/fonts/cover/numbers/contents), or
 * "template" (the user's own Word file). Every export event carries it as
 * `setup`, so adoption of the setup panel has a denominator.
 */
let documentSetupParam: 'default' | 'custom' | 'template' = 'default';
export const setDocumentSetupParam = (value: 'default' | 'custom' | 'template'): void => {
  documentSetupParam = value;
};
const setupParams = (): Record<string, string> => ({ setup: documentSetupParam });

// One control changed in the Setup panel. `choice` is the new value (or
// set/removed/too_large/not_docx for the template) — never file names.
export const trackDocumentSetupChanged = (setting: string, choice: string): void => {
  trackEvent('document_setup_changed', { setting, choice });
};

const handoffSiteParams = (): Record<string, string> => {
  try {
    const site = sessionStorage.getItem(HANDOFF_SITE_KEY);
    return site ? { site } : {};
  } catch {
    return {};
  }
};

/**
 * Workspace view modes (markdown-viewer targeting). `mode` is the pane
 * layout; `focus` is the chrome-hiding fullscreen-in-viewport toggle.
 * surface: 'toggle' = user clicked the switcher, 'deep_link' = arrived via
 * ?view=/?focus= (e.g. from the /markdown-viewer landing).
 */
export const trackViewModeChanged = (
  mode: 'split' | 'editor' | 'preview',
  focus: boolean,
  surface: 'toggle' | 'deep_link'
): void => {
  trackEvent('view_mode_changed', {
    mode,
    focus: focus ? 'on' : 'off',
    surface,
  });
};

export const trackPreviewPrinted = (mode: string): void => {
  trackEvent('preview_printed', { mode });
};

/** Editor formatting toolbar / keyboard shortcuts (markdown-editor intent). */
export const trackFormattingApplied = (
  action: string,
  via: 'toolbar' | 'shortcut'
): void => {
  trackEvent('formatting_applied', { action, via });
};

/**
 * The "this looks like a generator script" hint (issue #30 pattern: the AI
 * answered with python-docx CODE instead of document content, and the user
 * exported a code listing). `action` is an already-registered dimension.
 */
export const trackGeneratorHint = (action: "shown" | "dismissed"): void => {
  trackEvent("generator_code_hint", { action });
};

/**
 * The "llm2doc was updated" notice that holds an export made from a page
 * loaded before the latest deploy (lib/use-newer-build.ts). `action` and
 * `format` are already-registered dimensions.
 */
export const trackUpdatePrompt = (action: "shown" | "reload" | "export_anyway", format: string): void => {
  trackEvent("update_prompt", { action, format });
};

/** Mermaid diagram render attempt in the preview (viewer fidelity). */
export const trackMermaidRendered = (success: boolean): void => {
  trackEvent('mermaid_rendered', { success });
};

/** Contents dropdown navigation in the preview (long-document viewing). */
export const trackTocNavigated = (): void => {
  trackEvent('toc_navigated', {});
};

export const trackExportClicked = (
  format: string,
  inputFormat: 'markdown'
): void => {
  trackEvent('export_clicked', {
    format,
    input_format: inputFormat,
    ...handoffSiteParams(),
    ...setupParams(),
  });
};

export const trackExportSuccess = (
  format: string,
  inputFormat: string,
  contentLength: number,
  processingTime?: number,
  docSize?: DocSize,
): void => {
  trackEvent('export_success', {
    format,
    input_format: inputFormat,
    content_length: contentLength,
    processing_time_ms: processingTime,
    ...(docSize ? { doc_size: docSize } : {}),
    ...handoffSiteParams(),
    ...setupParams(),
  });
};

export const trackExportFailure = (
  format: string,
  inputFormat: string,
  errorMessage: string,
  errorType?: string,
  docSize?: DocSize,
): void => {
  trackEvent('export_failure', {
    format,
    input_format: inputFormat,
    error_message: errorMessage,
    ...(docSize ? { doc_size: docSize } : {}),
    // `error_type` is a REGISTERED dimension; `error_message` is not, so a
    // failure is only queryable in GA4 through this field. Server-classified
    // codes land here (see lib/server/conversion-error.ts) — the event is the
    // record that outlives Render's log retention.
    error_type: errorType,
    ...handoffSiteParams(),
    ...setupParams(),
  });
};

/**
 * The server DOCX path failed and the browser-side converter produced the file
 * instead. That converter cannot emit Office Math: equations come out as
 * literal LaTeX text, so on a document with math this is a degraded export,
 * not a clean one (issue #22/#23 — two "equations" complaints in two days
 * while server-side conversion was provably correct).
 *
 * Tracked because `export_success` still fires for these downloads, which made
 * the completion rate read ~100% while users were receiving broken files.
 * `has_latex` is the parameter that separates "harmless" from "user-visible".
 */
export const trackExportDegraded = (
  format: string,
  inputFormat: string,
  hasLatex: boolean,
  reason: string,
  // reason "math_parity" carries math_expected and math_missing — the
  // counts the Word route returned (lib/server/math-parity.ts) — and since
  // 2026-10-28 math_tex_failed + math_shape (which formulas texmath refused,
  // as control sequences) and doc_size.
  extra: Record<string, number | string> = {},
): void => {
  trackEvent('export_degraded', {
    format,
    input_format: inputFormat,
    has_latex: hasLatex,
    error_type: reason,
    ...extra,
    ...handoffSiteParams(),
    ...setupParams(),
  });
};

// ============================================================================
// Batch Converter Events (HIGH PRIORITY)
// ============================================================================

export const trackBatchFilesSelected = (
  fileCount: number,
  totalSizeMB: number
): void => {
  trackEvent('batch_files_selected', {
    file_count: fileCount,
    total_size_mb: Math.round(totalSizeMB * 100) / 100,
  });
};

export const trackBatchFormatSelected = (
  format: 'docx' | 'pdf' | 'html' | 'markdown'
): void => {
  trackEvent('batch_format_selected', {
    format,
  });
};

export const trackBatchConversionStarted = (
  fileCount: number,
  outputFormat: string
): void => {
  trackEvent('batch_conversion_started', {
    file_count: fileCount,
    output_format: outputFormat,
  });
};

export const trackBatchConversionCompleted = (
  fileCount: number,
  successCount: number,
  failedCount: number,
  totalTimeMs: number
): void => {
  trackEvent('batch_conversion_completed', {
    file_count: fileCount,
    success_count: successCount,
    failed_count: failedCount,
    total_time_ms: totalTimeMs,
    success_rate: Math.round((successCount / fileCount) * 100),
  });
};

export const trackBatchFileRemoved = (remainingFiles: number): void => {
  trackEvent('batch_file_removed', {
    remaining_files: remainingFiles,
  });
};

export const trackBatchCleared = (filesCleared: number): void => {
  trackEvent('batch_cleared', {
    files_cleared: filesCleared,
  });
};

export const trackBatchZipDownloaded = (
  fileCount: number,
  format: string
): void => {
  trackEvent('batch_zip_downloaded', {
    file_count: fileCount,
    format,
  });
};

// ============================================================================
// Homepage Converter Events (MEDIUM PRIORITY)
// ============================================================================

// Fires when the user picks a primary export format from the export-bar
// pills (DOCX / PDF / HTML / MD). The selection is the format the big
// "Export" button will use; switching is one of the most signal-rich
// interactions on the homepage because it tells us which output users
// actually prefer.
export const trackFormatToggle = (
  format: string,
  surface: 'export_bar' | 'more_formats' = 'export_bar'
): void => {
  trackEvent('format_toggle', {
    format,
    surface,
  });
};

export const trackExampleLoaded = (
  exampleType: 'article' | 'table' | 'codeBlock' | 'mixed' | string
): void => {
  trackEvent('example_loaded', {
    example_type: exampleType,
  });
};

// "Copy formatted" / "Copy plain text" from the preview toolbar — the answer
// to the most common complaint about AI output ("literal asterisks in Word").
export type CopyMode = 'formatted' | 'plain';

// The Excel chip on a rendered table: "this table" or "all tables" as .xlsx.
// (The picker's Excel format goes through the normal export_* events.)
export const trackTableExport = (scope: 'table' | 'all', tableCount: number): void => {
  trackEvent('table_export', {
    format: 'xlsx',
    scope,
    table_count: tableCount,
  });
};

// A chat share link pasted into the editor / opened via ?share= / submitted
// on a landing. `result` is the API's closed-vocabulary code ("ok",
// "unsupported_site", "not_found", "blocked", …) — never page content.
export const trackShareImport = (
  site: string,
  result: string,
  messageCount: number,
  surface: string = 'converter_paste',
  docSize?: DocSize,
  canvas?: { found: number; missing: number },
): void => {
  trackEvent('share_import', {
    site,
    result,
    message_count: messageCount,
    surface,
    ...(docSize ? { doc_size: docSize } : {}),
    // Gemini only: how many Canvas placeholders found their document and how
    // many did not (counts, never content) — two thirds were missing in
    // October 2026 and the counts say whether that is whole conversations
    // or single turns.
    ...(canvas ? { canvas_found: canvas.found, canvas_missing: canvas.missing } : {}),
  });
};

// Formatted text (Word, Google Docs, a web page) pasted into the editor and
// turned into Markdown as it landed. content_paste fires as well.
export const trackRichPaste = (contentLength: number): void => {
  trackEvent('rich_paste_converted', { content_length: contentLength });
};

export const trackContentCopied = (mode: CopyMode, contentLength: number): void => {
  trackEvent('content_copied', {
    mode,
    content_length: contentLength,
  });
};

export const trackCopyMarkdown = (source: 'html' | 'preview'): void => {
  trackEvent('copy_markdown', {
    source,
  });
};

// Where a paste came in — the converter's own editor or a landing page's
// paste box (which hands the text to the converter via localStorage).
export type PasteSurface =
  | 'converter'
  | 'landing_markdown_to_word'
  | 'landing_markdown_to_pdf'
  | 'landing_live_preview'
  | 'landing_latex_to_word'
  | 'landing_chatgpt_to_pdf'
  | 'landing_gemini_to_pdf'
  | 'landing_claude_to_pdf'
  | 'landing_claude_to_word'
  | 'landing_gemini_to_word'
  | 'landing_deepseek_to_pdf'
  | 'landing_grok_to_pdf'
  | 'landing_markdown_to_google_docs'
  | 'landing_markdown_to_excel'
  | 'landing_markdown_to_html'
  | 'landing_markdown_to_epub'
  | 'landing_html_to_markdown'
  | 'guide_chatgpt_to_word'
  | 'guide_chatgpt_to_pdf'
  | 'guide_paste_chatgpt_into_word';

export const trackContentPaste = (
  inputFormat: 'markdown',
  contentLength: number,
  surface: PasteSurface = 'converter'
): void => {
  trackEvent('content_paste', {
    input_format: inputFormat,
    content_length: contentLength,
    surface,
  });
};

// Click-to-copy of a rendered equation as Word-compatible MathML.
export const trackEquationCopied = (displayMode: boolean): void => {
  trackEvent('equation_copied', {
    display_mode: displayMode ? 'block' : 'inline',
  });
};

// Single-file upload into the converter (distinct from batch_files_selected).
// surface: where the file came in — the converter itself or a landing page.
export type UploadSurface =
  | 'converter'
  | 'landing_markdown_to_word'
  | 'landing_markdown_viewer'
  | 'landing_markdown_to_pdf'
  | 'landing_live_preview'
  | 'landing_latex_to_word'
  | 'landing_chatgpt_to_pdf'
  | 'landing_claude_to_pdf'
  | 'landing_claude_to_word'
  | 'landing_gemini_to_word'
  | 'landing_deepseek_to_pdf'
  | 'landing_grok_to_pdf'
  | 'landing_markdown_to_google_docs'
  | 'landing_markdown_to_excel'
  | 'landing_markdown_to_html'
  | 'landing_markdown_to_epub'
  | 'landing_word_to_markdown'
  | 'landing_html_to_markdown';

export const trackFileUploaded = (
  contentLength: number,
  surface: UploadSurface,
  // markdown (read in the browser) or the format the server converted: docx | odt | rtf | html
  inputFormat: string = 'markdown'
): void => {
  trackEvent('file_uploaded', {
    input_format: inputFormat,
    content_length: contentLength,
    surface,
  });
};

export const trackHistoryReopened = (format: string): void => {
  trackEvent('history_reopened', { format });
};

export const trackHistoryCleared = (itemsCleared: number): void => {
  trackEvent('history_cleared', {
    items_cleared: itemsCleared,
  });
};

// ============================================================================
// Navigation Events (MEDIUM PRIORITY)
// ============================================================================

export const trackNavigation = (
  destination: string,
  linkText: string,
  section?: 'header' | 'footer' | 'cta' | 'guide'
): void => {
  trackEvent('nav_clicked', {
    destination,
    link_text: linkText,
    section,
  });
};

export const trackThemeToggle = (theme: 'light' | 'dark'): void => {
  trackEvent('theme_toggled', {
    theme,
  });
};

export const trackMobileMenuOpened = (): void => {
  trackEvent('mobile_menu_opened');
};

export const trackMobileMenuClosed = (): void => {
  trackEvent('mobile_menu_closed');
};

export const trackGuideClicked = (
  guideSlug: string,
  guideTitle: string
): void => {
  trackEvent('guide_clicked', {
    guide_slug: guideSlug,
    guide_title: guideTitle,
  });
};

export const trackCTAClicked = (
  buttonText: string,
  page: string,
  destination: string
): void => {
  trackEvent('cta_clicked', {
    button_text: buttonText,
    page,
    destination,
  });
};

// ============================================================================
// Engagement Events (LOWER PRIORITY)
// ============================================================================

export const trackFAQExpanded = (question: string): void => {
  trackEvent('faq_expanded', {
    question: question.substring(0, 100), // Limit length
  });
};

export const trackScrollDepth = (
  depthPercentage: 25 | 50 | 75 | 100,
  page: string
): void => {
  trackEvent('scroll_depth', {
    depth_percentage: depthPercentage,
    page,
  });
};

export const trackEngagedSession = (
  page: string,
  timeSeconds: number
): void => {
  trackEvent('engaged_session', {
    page,
    time_seconds: timeSeconds,
  });
};

// ============================================================================
// Error Tracking
// ============================================================================

export const trackError = (
  errorType: string,
  errorMessage: string,
  location?: string
): void => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 150), // Limit length
    location,
  });
};

// ============================================================================
// ENHANCED TRACKING - Content Analysis
// ============================================================================

export interface ContentStats {
  wordCount: number;
  hasLatex: boolean;
  hasTables: boolean;
  hasCodeBlocks: boolean;
  hasImages: boolean;
  listCount: number;
  headingCount: number;
}

/**
 * Does this document contain real math?
 *
 * The old test — `/\$\$|\\\[|\\\(|\\begin\{/` — saw display math, ChatGPT
 * delimiters and environments but MISSED plain `$…$` inline math, which is the
 * commonest form in AI chat output: both documents attached to issues #22 and
 * #23 use it exclusively, so every event for them reported `has_latex: false`.
 *
 * A naive `/\$[^$]+\$/` is not the fix — it reads "costs $5 and $10" as a
 * formula. Preprocessing first is: it escapes currency to `\$` (and normalises
 * `\(…\)` to `$…$`), so whatever `$` survives is math by construction. That
 * also makes this agree with what the converters actually render, rather than
 * being a second, drifting opinion.
 *
 * `\begin{…}` is checked directly because preprocessing leaves environments
 * untouched and they need no dollar delimiters.
 */
const contentHasMath = (content: string): boolean => {
  if (/\\begin\{/.test(content)) return true;
  try {
    return containsDollarMath(preprocessMarkdown(content));
  } catch {
    // Analytics must never break an export.
    return /\$\$|\\\[|\\\(/.test(content);
  }
};

/**
 * Analyze content and return statistics
 */
export const analyzeContent = (content: string): ContentStats => {
  return {
    wordCount: countWords(content),
    hasLatex: contentHasMath(content),
    hasTables: /\|.*\|/.test(content) || /<table/i.test(content),
    hasCodeBlocks: /```|<pre|<code/i.test(content),
    hasImages: /!\[.*\]\(|<img/i.test(content),
    listCount: (content.match(/^[\s]*[-*+]\s+|\d+\.\s+|<[uo]l/gim) || []).length,
    headingCount: (content.match(/^#{1,6}\s+|<h[1-6]/gim) || []).length,
  };
};

/**
 * Track content characteristics at export time
 */
export const trackContentAnalysis = (
  inputFormat: 'markdown',
  stats: ContentStats
): void => {
  trackEvent('content_analyzed', {
    input_format: inputFormat,
    word_count: stats.wordCount,
    has_latex: stats.hasLatex,
    has_tables: stats.hasTables,
    has_code_blocks: stats.hasCodeBlocks,
    has_images: stats.hasImages,
    list_count: stats.listCount,
    heading_count: stats.headingCount,
    content_complexity: stats.wordCount > 1000 ? 'complex' : stats.wordCount > 300 ? 'medium' : 'simple',
    doc_size: docSizeBucket(stats.wordCount),
  });
};

// ============================================================================
// ENHANCED TRACKING - Detailed Error & Performance
// ============================================================================

export type ErrorType =
  | 'latex_parse'
  | 'table_format'
  | 'image_load'
  | 'pandoc_timeout'
  | 'memory'
  | 'network'
  | 'conversion'
  | 'clipboard'
  // A tab opened before a deploy could not load an exporter chunk the new
  // build no longer serves (lib/converters/lazy.ts) — the fix is a reload.
  | 'stale_build'
  // The browser PDF builder (lib/converters/pdf.ts): a page the canvas would
  // not draw or encode, a document that rendered blank, an empty document.
  | 'pdf_render'
  | 'pdf_blank'
  | 'empty_document'
  | 'other'
  // Server-classified conversion failures, mirroring ConversionErrorCode in
  // lib/server/conversion-error.ts. Deliberately a closed set: Pandoc quotes
  // the offending input in its errors, so only codes cross the wire.
  | 'pandoc_missing'
  | 'pandoc_oom'
  | 'pandoc_exit'
  | 'tmp_io'
  | 'unknown'
  // The server refused the request: the document exceeds MAX_MARKDOWN_CHARS
  // (lib/server/limits.ts). Not retried, not degraded — surfaced to the user.
  | 'too_large'
  // Over MAX_PANDOC_CHARS (~200 pages) for a Pandoc format: DOCX, PPTX, EPUB.
  | 'too_long'
  // The heavy-work queue kept refusing (queue full, waited too long, no
  // memory to start) through every retry — lib/server/serial-queue.ts.
  | 'server_busy';

export const trackErrorDetailed = (
  errorType: ErrorType,
  errorContext: string,
  userAction: string,
  format?: string
): void => {
  trackEvent('error_detailed', {
    error_type: errorType,
    error_context: errorContext.substring(0, 200),
    user_action: userAction,
    // Without this, a conversion failure could not be attributed to a format
    // without cross-referencing export_failure by timestamp — which is how the
    // 2026-08-06 PPTX failures stayed anonymous.
    format,
  });
};

export type PerformanceMetric =
  | 'slow_conversion'
  | 'large_file'
  | 'browser_warning'
  | 'memory_warning';

export const trackPerformanceWarning = (
  metric: PerformanceMetric,
  value: number,
  context: string
): void => {
  trackEvent('performance_warning', {
    metric,
    value,
    context,
  });
};

// ============================================================================
// ENHANCED TRACKING - User Session & Retention
// ============================================================================

export const trackSessionStart = (
  referrer: string,
  landingPage: string
): void => {
  trackEvent('session_start', {
    referrer: referrer || 'direct',
    landing_page: landingPage,
    device_type: typeof navigator !== 'undefined' && /mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
  });
};

export const trackFirstConversion = (
  timeToConvertMs: number,
  format: string
): void => {
  trackEvent('first_conversion', {
    time_to_convert_seconds: Math.round(timeToConvertMs / 1000),
    format,
    ...handoffSiteParams(),
    ...setupParams(),
  });
};

export type UserType = 'new' | 'returning' | 'power_user';

export const trackReturnUser = (
  visitCount: number,
  daysSinceLastVisit: number
): void => {
  const userType: UserType = visitCount === 1 ? 'new' : visitCount < 5 ? 'returning' : 'power_user';

  trackEvent('user_return', {
    visit_count: visitCount,
    days_since_last_visit: daysSinceLastVisit,
    user_type: userType,
  });
};

// ============================================================================
// ENHANCED TRACKING - Content Editing & Engagement
// ============================================================================

export type EditType = 'paste' | 'type' | 'clear' | 'load_example';

export const trackContentEdited = (
  inputFormat: 'markdown',
  editType: EditType,
  contentLength: number
): void => {
  trackEvent('content_edited', {
    input_format: inputFormat,
    edit_type: editType,
    content_length: contentLength,
  });
};

export type PreviewAction = 'scroll' | 'expand' | 'collapse' | 'copy';

export const trackPreviewInteraction = (
  action: PreviewAction,
  timeSpentSeconds: number
): void => {
  trackEvent('preview_interaction', {
    action,
    time_spent_seconds: timeSpentSeconds,
  });
};

export const trackPageEngagement = (
  page: string,
  maxScrollDepth: number,
  timeOnPageSeconds: number,
  interacted: boolean
): void => {
  trackEvent('page_engagement', {
    page,
    max_scroll_depth: Math.round(maxScrollDepth),
    time_on_page_seconds: timeOnPageSeconds,
    interacted,
  });
};

// ============================================================================
// ENHANCED TRACKING - A/B Testing & Experiments
// ============================================================================

export const trackExperimentView = (
  experimentName: string,
  variant: string
): void => {
  trackEvent('experiment_viewed', {
    experiment_name: experimentName,
    variant,
  });
};

export const trackFeatureFlagEnabled = (
  flagName: string,
  enabled: boolean
): void => {
  trackEvent('feature_flag', {
    flag_name: flagName,
    enabled,
  });
};

// ============================================================================
// ENHANCED TRACKING - Quality Feedback & Social
// ============================================================================

export const trackConversionFeedback = (
  format: string,
  rating: 1 | 2 | 3 | 4 | 5,
  issues?: string[],
  /** Whether the "attach my markdown" box was ticked (dislikes only). */
  attached?: boolean,
  /** `widget`: the 👍/👎 prompt; `report`: the "Send the document" button on a flawed export (since 2026-10-28). */
  surface: 'widget' | 'report' = 'widget',
): void => {
  trackEvent('conversion_feedback', {
    format,
    rating,
    issues: issues?.join(','),
    attached: attached === undefined ? undefined : attached ? 'yes' : 'no',
    surface,
  });
};

export type ShareMethod = 'copy_link' | 'twitter' | 'email' | 'other';
export type ShareContext = 'header' | 'post_conversion' | 'footer';

export const trackShare = (
  method: ShareMethod,
  context: ShareContext
): void => {
  trackEvent('content_shared', {
    method,
    context,
  });
};

// ============================================================================
// ENHANCED TRACKING - Help & Feature Discovery
// ============================================================================

export type HelpType = 'tooltip' | 'guide_link' | 'info_banner' | 'faq';

export const trackHelpViewed = (
  helpType: HelpType,
  location: string
): void => {
  trackEvent('help_viewed', {
    help_type: helpType,
    location,
  });
};

export const trackKeyboardShortcut = (
  shortcut: string,
  action: string
): void => {
  trackEvent('keyboard_shortcut_used', {
    shortcut,
    action,
  });
};

// ============================================================================
// ENHANCED TRACKING - Batch Converter Per-File
// ============================================================================

export const trackBatchFileResult = (
  fileName: string,
  fileSize: number,
  format: string,
  success: boolean,
  processingTimeMs: number
): void => {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'unknown';

  trackEvent('batch_file_processed', {
    file_extension: extension,
    file_size_kb: Math.round(fileSize / 1024),
    format,
    success,
    processing_time_ms: Math.round(processingTimeMs),
  });
};
